import { Component, ChangeDetectionStrategy, signal, inject, Injectable } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { IrisDmsFoldersSandbox } from '@iris/common/modules/dms/sandbox/dms-folders.sandbox';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { IrisDmsRootFolder } from '@iris/common/modules/dms/models/IrisDMSFolder';
import { catchError, debounceTime, filter, switchMap, tap } from 'rxjs/operators';
import { IrisFoldersService } from '@iris/common/services/docs/folders.service';
import { of } from 'rxjs';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { StrakitButtonComponent, StrakitTextComponent } from '@ngx-strakit/cdk/controls';
import { MatInputModule } from '@angular/material/input';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UpperCasePipe } from '@angular/common';
import { IrisLoadingButtonDirective } from '@iris/common/modules/directives-common/directives/iris-loading-button.directive';
import { FormGroupType } from '@iris/common/utils/reactive-form.utils';
import { applyValidationMessageResolvers, ValidationMessageResolver } from '@ngx-strakit/validation';
import {
  ConfirmByKeypressDirective,
  SkModalBase,
  SkModalContentComponent,
  StrakitButtonPrefixDirective,
} from '@ngx-strakit/cdk';

interface AddFolderFormI {
  name: string;
}
interface AddFolderModalDataI {
  parentFolderId: string;
  setSelected: boolean;
}

@Injectable()
export class IrisDmsFolderExistsValidationMessageResolver implements ValidationMessageResolver {
  private readonly translate = inject(TranslateService);
  readonly errorCode = 'folderExists';

  getMessage(): string {
    return this.translate.instant('label.dms.FolderWithThisNameExists');
  }
}

@Component({
  selector: 'iris-dms-folder-add-modal',
  imports: [
    TranslatePipe,
    UpperCasePipe,
    ReactiveFormsModule,

    MatInputModule,

    SkModalContentComponent,

    StrakitTextComponent,
    ConfirmByKeypressDirective,
    IrisLoadingButtonDirective,
    StrakitButtonComponent,
    StrakitButtonPrefixDirective,
  ],
  templateUrl: './folder-add-modal.component.html',
  styleUrls: ['./folder-add-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    ...applyValidationMessageResolvers(
      IrisDmsFolderExistsValidationMessageResolver,
    ),
  ],
})
export class IrisDmsFolderAddModalComponent extends SkModalBase<IrisDmsFolderAddModalComponent, void> {
  readonly dialogRef: MatDialogRef<IrisDmsFolderAddModalComponent>;

  private readonly dmsFoldersSandbox = inject(IrisDmsFoldersSandbox);
  private readonly dmsFolderService = inject(IrisFoldersService);

  private readonly data = inject<AddFolderModalDataI>(MAT_DIALOG_DATA);
  readonly parentFolderId = signal<string>(this.data.parentFolderId);

  readonly addFolderForm = new FormGroup<FormGroupType<AddFolderFormI>>({
    name: new FormControl('', [Validators.required]),
  });

  readonly duplicateChecking = signal(false);

  constructor() {
    super();

    this.name.valueChanges.pipe(
      filter(Boolean),
      filter(() => this.parentFolderId() !== IrisDmsRootFolder.id),
      tap(() => this.duplicateChecking.set(true)),
      debounceTime(1000),
      switchMap((folderName: string) => this.dmsFolderService.checkFolderExists(this.parentFolderId(), folderName)),
      tap((exists) => {
        if (exists) {
          this.name.setErrors({ 'folderExists': true });
          this.name.markAsTouched();
        } else {
          this.name.setErrors(null);
        }
        this.duplicateChecking.set(false);
      }),
      catchError(() => {
        this.duplicateChecking.set(false);
        return of(false);
      }),
      takeUntilDestroyed(),
    ).subscribe();
  }

  get name(): AbstractControl {
    return this.addFolderForm.controls.name;
  }

  onSubmit(): void {
    if (this.addFolderForm.invalid) { return; }
    this.dmsFoldersSandbox.createFolder(this.addFolderForm.value, this.parentFolderId(), this.data.setSelected);
    this.closeWithoutResult();
  }
}
